import React from 'react';
import { Edit, Trash2, Power, WifiOff, AlertCircle, ThermometerSnowflake, Droplets, Wind } from 'lucide-react';

const DeviceCard = ({ device, deviceType, onEdit, onDelete, onToggle }) => {
  // Handle null or missing values
  const readingValue = device.lastReading?.value !== undefined ? device.lastReading.value : '--';
  
  // Calculate if data is stale (more than 10 minutes old)
  const isDataStale = device.lastUpdated ? 
    (new Date() - new Date(device.lastUpdated)) > 10 * 60 * 1000 : 
    true;
  
  // Determine if device is controllable
  const isControllable = device.mqttTopics?.command && deviceType?.canControl;
  
  // Determine icon based on device type
  const getDeviceIcon = () => {
    if (!deviceType) return <AlertCircle size={18} />;
    
    switch(deviceType.typeName.toLowerCase()) {
      case 'temperature':
      case 'thermometer':
        return <ThermometerSnowflake size={18} />;
      case 'humidity':
        return <Droplets size={18} />;
      case 'airflow':
      case 'fan':
        return <Wind size={18} />;
      default:
        return <AlertCircle size={18} />;
    }
  };
  
  // Determine reading unit based on device type
  const getReadingUnit = () => {
    if (!deviceType) return '';
    
    switch(deviceType.typeName.toLowerCase()) {
      case 'temperature':
      case 'thermometer':
        return '°F';
      case 'humidity':
        return '%';
      case 'co2':
        return 'ppm';
      default:
        return '';
    }
  };
  
  return (
    <div className="col-lg-3 col-md-6 mb-4">
      <div className={`card h-100 ${isDataStale ? 'border-warning' : ''}`}>
        <div className="card-header py-2 d-flex justify-content-between align-items-center">
          <h6 className="card-title mb-0">{device.deviceName}</h6>
          <div className="btn-group btn-group-sm">
            {isControllable && (
              <button
                onClick={() => onToggle(device.deviceID, !device.isOn)}
                className={`btn btn-sm ${
                  device.isOn ? 'btn-primary' : 'btn-outline-secondary'
                }`}
                title="Toggle Device"
              >
                <Power size={14} />
              </button>
            )}
            <button 
              className="btn btn-sm btn-outline-secondary" 
              onClick={() => onEdit(device)}
              title="Edit Device"
            >
              <Edit size={14} />
            </button>
            <button 
              className="btn btn-sm btn-outline-danger" 
              onClick={() => onDelete(device.deviceID)}
              title="Remove Device"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        <div className="card-body py-2">
          {isDataStale && (
            <div className="alert alert-warning py-1 mb-2" style={{fontSize: '0.8rem'}}>
              <small className="d-flex align-items-center">
                <AlertCircle size={12} className="me-1" />
                Data may be stale
              </small>
            </div>
          )}
          
          <div className="d-flex align-items-center mb-2">
            {getDeviceIcon()}
            <div className="ms-2 fw-bold">
              {readingValue !== '--' ? `${readingValue}${getReadingUnit()}` : 'No data'}
            </div>
            <div className="ms-auto small">
              {deviceType?.typeName || 'Unknown Type'}
            </div>
          </div>
          
          {/* Display historical data if available */}
          {device.history && device.history.length > 0 && (
            <div className="small text-muted mb-2">
              24hr: Min {device.history.min}{getReadingUnit()} / 
              Avg {device.history.avg}{getReadingUnit()} / 
              Max {device.history.max}{getReadingUnit()}
            </div>
          )}
          
          {/* Target value if applicable */}
          {device.targetValue !== undefined && (
            <div className="small mb-2">
              Target: {device.targetValue}{getReadingUnit()}
            </div>
          )}
          
          <div className="d-flex justify-content-between align-items-center">
            {device.location && <small className="text-muted">{device.location}</small>}
            {device.lastUpdated && (
              <small className="text-muted" style={{fontSize: '0.7rem'}}>
                {new Date(device.lastUpdated).toLocaleTimeString()}
              </small>
            )}
          </div>
          
          {/* Status indicator */}
          {device.status && (
            <div className="mt-2">
              <span className={`badge ${device.isOnline ? 'bg-success' : 'bg-danger'}`}>
                {device.isOnline ? 'Online' : 'Offline'}
              </span>
              {device.batteryLevel !== undefined && (
                <span className="badge bg-secondary ms-1">
                  Battery: {device.batteryLevel}%
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeviceCard;